import moment from 'moment';
import UuidV4 from 'uuid/v4';

import Aws from '../services/aws';
import ForumService from '../services/forum';


/**
 * Formats a date relative to a reference date (defaults to current date),
 * making the resulting string as short as possible.
 *
 * Formatting rules:
 *   Within a minute:  "1m"
 *   Within an hour:   "14m"
 *   Within a day:     "14h"
 *   Within a month:   "14d"
 *   Within same year: "Jan 14"
 *   Else:             "Nov '14"
 *
 * @param {Date}   date
 * @param {object} [options]
 * @param {Date}   options.[refDate=new Date]
 */
export function formatRecentDate(date, options) {
    let now = options && options.refDate;
    if (!now) {
        now = new Date;
    }

    const duration = moment.duration(moment(now).diff(date));
    if (duration.asSeconds() < 60) {
        return '1m';
    }
    if (duration.asMinutes() < 60) {
        return `${Math.floor(duration.asMinutes())}m`;
    }
    if (duration.asHours() < 24) {
        return `${Math.floor(duration.asHours())}h`;
    }
    if (duration.asMonths() < 1) {
        return `${Math.floor(duration.asDays())}d`;
    }
    if (date.getFullYear() === now.getFullYear()) {
        return moment(date).format('MMM D');
    }
    return moment(date).format("MMM 'YY");
}


/**
 * Formats a date to show the full date and time.
 *
 * Example of format: "Jun 16, 2019 at 5:20 am"
 *
 * @param {Date}   date
 * @param {object} [options]
 * @param {string} options.[dateTimeSep='at']
 */
export function formatFullDate(date, options) {
    let dateTimeSep = 'at';
    if (options && options.dateTimeSep !== undefined) {
        dateTimeSep = options.dateTimeSep;
    }

    let format = 'MMM D, YYYY h:mm a';
    if (dateTimeSep) {
        format = `MMM D, YYYY [${dateTimeSep}] h:mm a`;
    }

    return moment(date).format(format);
}


/**
 * Accepts a thread title and makes it URL friendly.
 *
 * Sample input:  How To Model 3D Octopus?
 * Sample output: how-to-model-3d-octopus
 *
 * @param {string} threadTitle
 * @returns {string}
 */
export function formatTitlePreview(threadTitle) {
    // Implementation stolen from:
    // https://medium.com/@mhagemann/the-ultimate-way-to-slugify-a-url-string-in-javascript-b8e4a0d849e1
    return threadTitle
        .toLowerCase()
        .replace(/\s+/g, '-')     // Replace spaces with dashes
        .replace(/&/g, '-and-')   // Replace & with "and"
        .replace(/[^\w\-]+/g, '') // Remove all non-word characters
        .replace(/\-\-+/g, '-')   // Collapse multiple dashes
        .replace(/^-+/, '')       // Trim dash from start
        .replace(/-+$/, '');      // Trim dash from end
}


const prettySortOrderMap = {
    'postCount:asc': 'least-replies',
    'postCount:desc': 'most-replies',
    'viewCount:asc': 'least-views',
    'viewCount:desc': 'most-views',
    'lastPostCreatedAt:asc': 'oldest-activity',
    'lastPostCreatedAt:desc': 'newest-activity',
};

/**
 * Prettify sort order parameter for URL.
 *
 * Why? Consider the order "postCount:desc":
 *
 *   Without prettify: sort=postCount%3Adesc
 *   With prettify:    sort=most-replies
 *
 * Defaults to raw order if no pretty alternative is known.
 *
 * @param {string} order
 * @returns {string} str
 */
export function sortOrderToUrl(order) {
    const str = prettySortOrderMap[order];
    return str ? str : order;
}

/**
 * Uglify sort order parameter from URL.
 *
 * Assumes input is already ugly and is a valid order if no matching source is
 * found in map.
 *
 * @param {string} str
 * @returns {string} order
 */
export function sortOrderFromUrl(str) {
    for (const order in prettySortOrderMap) {
        if (prettySortOrderMap[order] === str) {
            return order;
        }
    }
    return str;
}


/**
 * Uploads content files to their respective forum S3 buckets:
 *
 *   images -> ttto-discussion-images-full
 *   videos -> ttto-discussion-videos
 *   files -> ttto-discussion-files
 *
 * Anything else is considered an error.
 *
 * @param {string} content
 * @param {object[]} contentFiles - see HtmlEditor
 * @returns {string} content with object URLs replaced by S3 URLs
 */
export async function uploadContentFiles(content, contentFiles) {

    async function uploadObjectToS3(bucket, key, object) {
        const signature = await Aws.signature(
            object,
            key,
            bucket,
        );
        const presignedUrl = await Aws.getPresignedUrl(
            this,
            bucket,
            key,
            'putObject',
        );
        const res = await Aws.upload(
            object,
            key,
            signature,
            bucket,
            presignedUrl.data,
        );
        return res.url;
    }
    async function uploadImageToS3(filename, file) {
        const bucket = 'ttto-discussion-images-full';
        return await uploadObjectToS3(bucket, filename, file);
    }
    async function uploadVideoToS3(filename, file) {
        const bucket = 'ttto-discussion-videos';
        return await uploadObjectToS3(bucket, filename, file);
    }
    async function uploadFileToS3(filename, file) {
        const bucket = 'ttto-discussion-files';
        return await uploadObjectToS3(bucket, filename, file);
    }

    // Start each upload
    const filePromises = [];
    for (const contentFile of contentFiles) {
        if (!content.includes(contentFile.objectUrl)) {
            continue;
        }
        const filename = UuidV4();
        const file = contentFile.object;
        let promise;
        switch (contentFile.kind) {
        case 'image':
            promise = uploadImageToS3(filename, file);
            break;
        case 'video':
            promise = uploadVideoToS3(filename, file);
            break;
        case 'file':
            promise = uploadFileToS3(filename, file);
            break;
        default:
            promise = Promise.reject(
                new Error(`unknown file kind: ${contentFile.kind}`)
            );
            break;
        }
        promise = promise.then(url => {
            content = content.replace(contentFile.objectUrl, url);
        });
        filePromises.push(promise);
    }

    // Wait for uploads to finish
    await Promise.all(filePromises);
    return content;
}


/**
 * Sorts categories into a predetermined order. Unknown types will be appended
 * to the end.
 *
 * @param {ForumCategory[]} categories
 * @returns {ForumCategory[]}
 */
export function sortCategories(categories) {
    const catNames = categories.map(c => c.name);
    const sortedCatNames = sortCategoryNames(catNames);
    return categories.sort((a, b) => {
        const ai = sortedCatNames.indexOf(a.name);
        const bi = sortedCatNames.indexOf(b.name);
        return ai - bi;
    });
}

/**
 * Sorts category names into a predetermined order. Unknown types will be
 * appended to the end.
 *
 * @param {string[]} categoryNames
 * @returns {string[]}
 */
export function sortCategoryNames(categoryNames) {
    categoryNames = categoryNames.slice();
    const preferredOrder = [
        'general',
        'maya',
        '3d-modeling',
        'texturing',
        'look-dev',
        'game-engines',
        'term-final-projects',
        'technical',
        'job-board',
        'off-topic',
    ];
    return categoryNames.sort((a, b) => {
        const ai = preferredOrder.indexOf(a);
        const bi = preferredOrder.indexOf(b);
        if (ai >= 0) {
            return bi >= 0 ? ai - bi : -1;
        } else {
            return bi >= 0 ? 1 : 0;
        }
    });
}


/**
 * A post window is a pointer to a subset of posts within a post store.
 *
 * For example, if a post store has 100 posts, the post window may point at
 * posts 20 to 39.
 */
export class PostWindow {
    constructor(postStore, offset, length) {
        this.postStore = postStore;
        this.offset = offset;
        this.length = length;
    }

    get loadedPosts() {
        if (this.isLoaded()) {
            let posts = [];
            for (let i = this.firstPostIndex; i <= this.lastPostIndex; ++i) {
                posts.push(this.postStore.findPost(i));
            }
            return posts;
        } else {
            return [];
        }
    }

    get firstPostIndex() {
        return this.offset;
    }

    get lastPostIndex() {
        return this.offset + this.length - 1;
    }

    isLoaded() {
        for (let i = this.firstPostIndex; i <= this.lastPostIndex; ++i) {
            if (!this.postStore.isPostLoaded(i)) {
                return false;
            }
        }
        return true;
    }

    hasPostsBefore() {
        return this.firstPostIndex > 0;
    }

    hasPostsAfter() {
        if (this.postStore.length === null) {
            return false;
        }
        return this.offset + this.length < this.postStore.length;
    }
}


/**
 * The post store encapsulates the loading of forum posts.
 *
 * The goal is to reduce the complexity of the ForumThread component a little
 * bit. Within a forum thread, the user can use the slider to navigate through
 * the thread non-linearly, meaning that several disjointed post windows may be
 * loaded at once. The post store should allow the forum thread to simply say
 * "load this post range" without having to worry about what's already been
 * loaded (or partially loaded), or if some of the posts have already been
 * requested but not yet received, etc.
 *
 * Unfortunately, when I started this I thought indexing by post index was a
 * good idea since we tend to load by range (ie. "upon page load, load the first
 * 20 posts"). This turned into a mess when it came to deleted posts, which is
 * why we tombstone everything now.
 *
 * Some of the methods revolve around "pages", which are just like fixed-length
 * post windows. Rather than loading specific posts individually, indexes will
 * be converted into page boundaries, and the whole page of posts will be
 * requested.
 *
 * (I'm so sorry, the implementation for this is far too complicated, and should
 * definitely be refactored.)
 */
export class PostStore {
    constructor(threadId) {
        // All of the arrays are indexed by post index.

        this.threadId = threadId;
        this.loadedPosts = []; // actual posts
        this.queuedPosts = []; // TODO: obsolete, should be refactored out
        this.length = null; // total known number of posts within the thread

        this._pageSize = 20; // number of posts to load per request
        this._loadingPosts = []; // promises responsible for loading a post
    }

    init(threadId) {
        this.threadId = threadId;
        // TODO: reset posts
    }

    /**
     * Rounds the given index to the page index that it corresponds to.
     *
     * For example: if index = 23 and page size = 20, this should return 20 so
     * that the page of posts 20 to 39 are requested.
     */
    _mapIndexToPage(index) {
        if (index < 0) {
            return 0;
        }
        if (this.length !== null && index >= this.length) {
            return Math.max(0, this.length - this._pageSize);
        }
        return Math.floor(index / this._pageSize) * this._pageSize;
    }

    /**
     * Request to load a range of posts. Posts shouldn't be loaded twice, and
     * the range should be clamped to something reasonable.
     */
    async _loadPosts(index, length) {

        // Clamp the index
        if (index < 0) {
            length += 0 - index;
            index = 0;
        }

        // Clamp the length
        if (this.length !== null &&
            this.length < index + length) {
            length = this.length - index;
        }
        if (length <= 0) {
            return;
        }

        // Verify that we have a thread id
        if (!this.threadId) {
            throw new Error('threadId required to load page');
        }

        // Verify that we haven't already loaded these posts
        let allLoaded = true;
        for (let i = index; i < index + length; ++i) {
            if (!this.isPostLoaded(i)) {
                allLoaded = false;
                break;
            }
        }
        if (allLoaded) {
            return;
        }

        // Verify that these posts aren't currently loading
        const getPostPromises = () => {
            let promises = [];
            for (let i = index; i < index + length; ++i) {
                promises.push(this._loadingPosts[i]);
            }
            return promises;
        };
        const allLoading = getPostPromises().findIndex(p => !p) === -1;
        if (allLoading) {
            return;
        }

        // Load the page
        const load = async () => {

            // Queue the new posts
            const postsAndCount = await ForumService.getPostsAndCount({
                threadId: this.threadId,
                limit: length,
                offset: index,
            });
            this.length = postsAndCount.count;
            this.queuedPosts.length = this.length;
            for (let i = 0; i < postsAndCount.posts.length; ++i) {
                this.queuedPosts[index + i] = postsAndCount.posts[i];
            }

            // Merge the queued posts
            let newLoadedPosts = this.loadedPosts.slice();
            newLoadedPosts.length = this.queuedPosts.length;
            this.queuedPosts.forEach((queuedPost, i) => {
                newLoadedPosts[i] = queuedPost;
            });
            this.loadedPosts = newLoadedPosts;
            this.queuedPosts.length = 0;
        };
        const loadingPromise = load();

        // Mark the posts that we're loading
        for (let i = index; i < index + length; ++i) {
            this._loadingPosts[i] = loadingPromise;
        }

        // Wait for our posts to load
        await loadingPromise;
    }

    _appendPost(post) {
        const index = this.length;
        const newLoadedPosts = this.loadedPosts.slice();
        newLoadedPosts[index] = post;
        this.loadedPosts = newLoadedPosts;
        this.length += 1;
    }

    _updatePost(id, fields) {
        const post = this.findPostById(id);
        Object.assign(post, fields);
    }

    /**
     * Load posts at and around the given index, determined by page boundaries.
     */
    async loadPageAt(index) {
        index = this._mapIndexToPage(index);
        await this._loadPosts(index, this._pageSize);
    }

    /**
     * Determine the page boundaries of the given index, and load the previous
     * page.
     */
    async loadPageBefore(index) {
        index = this._mapIndexToPage(index - 1);
        await this._loadPosts(index, this._pageSize);
    }

    /**
     * Determine the page boundaries of the given index, and load the next page.
     */
    async loadPageAfter(index) {
        index = this._mapIndexToPage(index + 1);
        await this._loadPosts(index, this._pageSize);
    }

    isPostLoaded(index) {
        return !!this.loadedPosts[index];
    }

    isPostByIdLoaded(id) {
        return !!this.loadedPosts.find(p => p && p.id === id);
    }

    findPost(index) {
        return this.loadedPosts[index];
    }

    findPostById(id) {
        return this.loadedPosts.find(p => p && p.id === id);
    }

    /**
     * Starting at the given index, determine the largest window of
     * non-disjointed posts and return it.
     *
     * For example, let's say we have there are 93 posts in total, and our page
     * size is 20. Only posts 20 - 59 are loaded.
     *
     * If we were to call getWindowAt(31), we would get a window containing the
     * loaded posts 20 - 59. If we were to call getWindowAt(60), we would get a
     * window containing the unloaded posts 60 - 93.
     *
     * TODO: the implementation can likely be simplified by considering that
     * posts are only ever loaded in fixed size pages.
     */
    getWindowAt(index, options) {
        if (index < 0 || this.length === null || index >= this.length) {
            return new PostWindow(this, index, 0);
        }

        const loaded = options && options.loaded || this.isPostLoaded(index);

        if (this.isPostLoaded(index) !== loaded) {
            return new PostWindow(this, index, 0);
        }

        let minIndex = index;
        while (minIndex > 0) {
            if (this.isPostLoaded(minIndex - 1) !== loaded) {
                break;
            }
            minIndex -= 1;
        }

        let maxIndex = index;
        while (maxIndex < this.length - 1) {
            if (this.isPostLoaded(maxIndex + 1) !== loaded) {
                break;
            }
            maxIndex += 1;
        }

        return new PostWindow(
            this,
            minIndex,
            maxIndex - minIndex + 1,
        );
    }

    getLoadedWindowAt(index) {
        return this.getWindowAt(index, { loaded: true });
    }

    getUnloadedWindowAt(index) {
        return this.getWindowAt(index, { loaded: false });
    }
}

/**
 * The thread utility class tries to wraps up all thread-related actions and
 * state into a single place. The methods will call the ForumService underneath,
 * and update the cached state upon success.
 */
export class Thread {
    constructor(threadId, userId, userRoles) {
        this.threadId = threadId;
        this.thread = null;
        this.timeline = null;

        this.actingUserId = userId;
        this.actingUserRoles = userRoles;
        this.actingUserLikedPosts = null;

        this.postStore = new PostStore(threadId);
    }

    /**
     * Allows you to reset the thread without creating a new instance. A better
     * name would probably be `reset`.
     *
     * You don't need to call this if you provided the same parameters in the
     * constructor.
     */
    init(threadId, userId, userRoles) {
        this.threadId = threadId;
        this.thread = null;
        this.timeline = null;
        this.actingUserId = userId;
        this.actingUserRoles = userRoles;
        this.actingUserLikedPosts = null;
        this.postStore.init(threadId);
    }

    /**
     * Loads the initial details required for operation.
     */
    async load() {
        const loadThreadDetails = async () => {
            this.thread = await ForumService.getThread(this.threadId);
        };

        const loadThreadTimeline = async () => {
            this.timeline = await ForumService.getThreadTimeline(this.threadId);
        };

        const loadUserDetails = async () => {
            this.actingUserLikedPosts = await ForumService.getLikedPosts(
                this.threadId,
                this.actingUserId,
            );
        };

        await Promise.all([
            loadThreadDetails(),
            loadThreadTimeline(),
            loadUserDetails(),
        ]);
    }

    /**
     * Creates a new thread view. Silently fails if the view has already been
     * recorded for this period.
     */
    async createView() {
        try {
            await ForumService.createThreadView(this.threadId);
            if (this.thread) {
                this.thread.viewCount += 1;
            }
        } catch (err) {
            if (!err.viewExists) {
                throw err;
            }
        }
    }

    /**
     * @param {object} post
     * @param {string} post.content
     * @param {number} post.[sourcePostId]
     */
    async createPost(post) {
        post = await ForumService.createPost(this.threadId, {
            content:      post.content,
            sourcePostId: post.sourcePostId,
        });
        this.postStore._appendPost(post);
        return post;
    }

    /**
     * @param {object} post
     * @param {number} post.id
     * @param {string} post.content
     */
    async updatePost(post) {
        const fields = {
            content: post.content,
        };
        post = await ForumService.updatePost(
            this.threadId,
            post.id,
            fields,
        );
        this.postStore._updatePost(post.id, post);
    }

    /**
     * @param {number} postId
     */
    async likePost(postId) {
        const threadId = this.threadId;
        const authorId = this.actingUserId;
        await ForumService.likePost(threadId, postId, authorId);

        if (!this.hasLikedPost(postId)) {
            this.actingUserLikedPosts.push({
                threadId,
                postId,
                authorId,
                createdAt: new Date,
            });
        }
    }

    /**
     * @param {number} postId
     */
    async unlikePost(postId) {
        const threadId = this.threadId;
        const authorId = this.actingUserId;
        await ForumService.unlikePost(threadId, postId, authorId);

        if (this.hasLikedPost(postId)) {
            this.actingUserLikedPosts = this.actingUserLikedPosts.filter(lp => {
                return (lp.threadId !== threadId ||
                        lp.postId !== postId ||
                        lp.authorId !== authorId);
            });
        }
    }

    /**
     * @param {number} postId
     */
    async deletePost(postId) {
        const threadId = this.threadId;
        const post = await ForumService.deletePost(threadId, postId);
        this.postStore._updatePost(post.id, post);
    }

    /**
     * @param {object}   fields
     * @param {string[]} fields.termTypes
     * @param {number[]} fields.pathIds
     * @param {number[]} fields.assignmentIds
     * @param {string[]} fields.tagNames
     */
    async updateThread(fields) {
        this.thread = await ForumService.updateThread(this.threadId, fields);
    }

    /**
     * @param {number} postId
     */
    async solveThread(postId) {
        const thread = await ForumService.solveThread(this.threadId, postId);
        this.thread.answerPost = thread.answerPost;
    }

    async unsolveThread() {
        await ForumService.unsolveThread(this.threadId);
        this.thread.answerPost = null;
    }

    get postCount() {
        if (this.postStore.length !== null) {
            return this.postStore.length;
        }
        if (this.thread !== null) {
            return this.thread.postCount;
        }
        return null;
    }

    get viewCount() {
        return this.thread ? this.thread.viewCount : null;
    }

    get firstDate() {
        return this.thread ? this.thread.firstPost.createdAt : null;
    }

    get lastDate() {
        return this.thread ? this.thread.lastPost.createdAt : null;
    }

    get answerPostId() {
        if (this.thread && this.thread.answerPost) {
            return this.thread.answerPost.id;
        } else {
            return null;
        }
    }

    get authorUserId() {
        return this.thread ? this.thread.author.id : null;
    }

    hasLikedPost(postId) {
        return (
            this.actingUserLikedPosts &&
            this.actingUserLikedPosts.some(lp => lp.postId === postId)
        );
    }

    isPostAnswer(postId) {
        return (
            this.thread &&
            this.thread.answerPost &&
            this.thread.answerPost.id === postId
        );
    }

    canEditPost(postId) {
        if (!this.thread) {
            return false;
        }
        const post = this.postStore.findPostById(postId);
        if (!post) {
            return false;
        }

        const allowedRoles = [
            'OnlineDirector',
            'Supervisor',
        ];
        const authByRole = this.actingUserRoles.some(r => {
            return allowedRoles.includes(r);
        });
        const authByUser = post.author && post.author.id === this.actingUserId;
        return authByRole || authByUser;
    }

    canDeletePost(postId) {
        return this.canEditPost(postId);
    }

    canEditThread() {
        if (!this.thread) {
            return false;
        }

        const allowedRoles = [
            'OnlineDirector',
            'Supervisor',
        ];
        const authByRole = this.actingUserRoles.some(r => {
            return allowedRoles.includes(r);
        });
        const authByUser = this.thread.author.id === this.actingUserId;
        return authByRole || authByUser;
    }

    canStickThread() {
        if (!this.thread) {
            return false;
        }

        const allowedRoles = [
            'OnlineDirector',
            'Supervisor',
        ];
        const authByRole = this.actingUserRoles.some(r => {
            return allowedRoles.includes(r);
        });
        return authByRole;
    }
}

/**
 * Convenience wrapper for paginated data sets.
 *
 * See further down for thread and post specializations.
 *
 * TODO: if this proves to be a useful abstraction, we should pull this into its
 * own util.
 */
export class PaginatedList {
    constructor(fetchFn, options) {
        this.options = Object.assign({
            pageSize: 20,
        }, options);
        this.items = this.options.initialItems || [];
        this.totalCount = null;
        this.fetchFn = fetchFn;

        this._loadingPromise = null;
    }

    isLoading() {
        return !!this._loadingPromise;
    }

    hasMore() {
        if (this.totalCount === null) {
            return true;
        }
        return this.totalCount - this.items.length > 0;
    }

    async loadMore() {
        if (this._loadingPromise) {
            return await this._loadingPromise;
        }

        const load = async () => {
            if (this.items.length === this.totalCount) {
                return;
            }

            let limit;
            if (this.totalCount === null) {
                limit = this.options.pageSize;
            } else {
                const countRemaining = this.totalCount - this.items.length;
                limit = Math.min(this.options.pageSize, countRemaining);
            }

            const offset = this.items.length;
            const res = await this.fetchFn(limit, offset);

            this.items = this.items.concat(res.items);
            this.totalCount = res.count;
        };

        try {
            this._loadingPromise = load();
            await this._loadingPromise;

        } finally {
            this._loadingPromise = null;
        }
    }

    /**
     * Searches the list for a particular item.
     *
     * The callback is passed the same parameters as `Array.find()`. The search
     * continues until either `true` is returned from the callback, or until
     * there are no more items to search.
     *
     * `undefined` is returned when no match is found.
     *
     * Unless `options.load == false`, more items are fetched as needed.
     *
     * @param {Function} callback
     * @param {object}   [options]
     * @param {boolean}  options.[load=true]
     * @returns {*}
     */
    async find(callback, options) {

        options = Object.assign({
            load: true,
        }, options);

        let lastIndex = -1;

        while (true) {

            let offset = lastIndex + 1;
            let index = this.items.slice(offset).findIndex(callback);
            if (index >= 0) {
                return this.items[offset + index];
            }

            lastIndex = this.items.length - 1;

            if (options.load && this.hasMore()) {
                await this.loadMore();
            } else {
                return undefined;
            }
        }
    }
}

export class PaginatedThreadList extends PaginatedList {
    constructor(params) {
        const fetchFn = async (limit, offset) => {
            const resParams = Object.assign({}, params, { limit, offset });
            const res = await ForumService.getThreadsAndCount(resParams);
            return {
                items: res.threads,
                count: res.count,
            };
        };
        super(fetchFn);
    }

    get threads() {
        return this.items;
    }

    /* See PaginatedList above for details.

    isLoading() {}

    hasMore() {}

    loadMore() {}

    */
}

export class PaginatedPostList extends PaginatedList {
    constructor(params) {
        const fetchFn = async (limit, offset) => {
            const resParams = Object.assign({}, params, { limit, offset });
            const res = await ForumService.getPostsAndCount(resParams);
            return {
                items: res.posts,
                count: res.count,
            };
        };
        super(fetchFn);
    }

    get posts() {
        return this.items;
    }

    /* See PaginatedList above for details.

    isLoading() {}

    hasMore() {}

    loadMore() {}

    */
}

export default {
    formatRecentDate,
    formatFullDate,
    formatTitlePreview,
    sortOrderToUrl,
    sortOrderFromUrl,
    uploadContentFiles,
    sortCategories,
    sortCategoryNames,
    PostWindow,
    PostStore,
    Thread,
    PaginatedList,
    PaginatedThreadList,
    PaginatedPostList,
}



// WEBPACK FOOTER //
// ./src/utils/forum.js